/*eslint-disable @typescript-eslint/no-unused-vars*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CustomTooltipWithLabel, downloadFile, FilterLayout, FilterProp, formatRelativeTimeForTable, LinkButton, SidePopover, TableDashboardTheme, TableLayout, useAppDispatch, useAppSelector } from '../../globalUtils/globalExports';
import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { chargerTypes, exportRuleExecutionSummary, fetchAdvanceRuleExecutionSummary, getAdvanceRuleEngineReducer, getCurrentFiltersValues, getFilterReducer, getFilterSelectedValue, getReferenceDataReducer, setAdvanceRuleExecutionSummaryTableProps, setFilterData } from '../../rmsReduxStore/reduxExports';
import { useNavigate } from 'react-router-dom';
import { IconWithTooltip } from '../../globalUtils/globalIcons';
import { Menu, MenuItem, ThemeProvider } from '@mui/material';
import { NoDataComponentRuleEngineHistory } from '../../globalUtils/TableDashboard/TableComponents';
import { cloneDeep } from 'lodash';
import './ExecutionSummaryPopover.css';
import { Id, toast } from 'react-toastify';
import { AxiosResponse } from 'axios';


interface IExecutionSummaryPopoverProps {
    ruleId: string;
    executionSummaryFiltersVisible: boolean;
    handleFiltersClosePopover: () => void;
    exportAnchor: [HTMLElement | null, React.Dispatch<React.SetStateAction<HTMLElement | null>>];
    showAllTriggers: boolean;
    setShowAllTriggers: React.Dispatch<React.SetStateAction<boolean>>;
}



const ExecutionSummaryPopover: React.FC<IExecutionSummaryPopoverProps> = (props) => {
    const { ruleId, executionSummaryFiltersVisible, handleFiltersClosePopover, exportAnchor, showAllTriggers, setShowAllTriggers } = props;
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const tableWrapRef = useRef<HTMLDivElement>(null);
    const { screenFilters } = useAppSelector(getFilterReducer);
    const { ruleExecutionSummary, ruleExecutionSummaryCount, ruleExecutionSummaryListTableProps, ruleExecutionSummaryLoader } = useAppSelector(getAdvanceRuleEngineReducer);
    const { chargingStationReferenceData, chargerVisibleIdReferenceData, advanceRuleEngineRuleNameReferenceData } = useAppSelector(getReferenceDataReducer);
    const ruleExecutionSummaryListTablePropsRef = useRef(ruleExecutionSummaryListTableProps);
    const [exportingFile, setExportingFile] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = exportAnchor;

    useEffect(() => {
        ruleExecutionSummaryListTablePropsRef.current = ruleExecutionSummaryListTableProps;
    }, [ruleExecutionSummaryListTablePropsRef])

    useEffect(() => {
        if (ruleExecutionSummaryListTablePropsRef?.current) {
            layoutCallback(
                ruleExecutionSummaryListTablePropsRef.current.pageNumber,
                ruleExecutionSummaryListTablePropsRef.current.pageSize,
                ruleExecutionSummaryListTablePropsRef.current.view,
                ruleExecutionSummaryListTablePropsRef.current.sortBy,
                ruleExecutionSummaryListTablePropsRef.current.order,
                null,
                null
            );
        }
    }, [showAllTriggers])

    const executionSummaryColumns = [
        {
            field: 'triggerTime',
            headerName: 'Trigger Time',
            renderHeader: ()=>{
                return (
                    <span className='version-releases-column-header'>
                    <span className='version-releases-column-header-text'>Trigger Time</span>
                    <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Time at which the Trigger Condition is met'}/></span>
                    </span>
                )
            },
            flex: 0.5,
            minWidth: 180,
            sortable: true,
            hideable: false,
            pinnable: false,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): React.ReactNode => {
                const rowData = params?.row;
                return rowData?.triggerTime ? formatRelativeTimeForTable(new Date(rowData?.triggerTime)) : '-';
            },
        },
        {
            field: 'fireTime',
            headerName: 'Fire Time',
            renderHeader: ()=>{
                return (
                    <span className='version-releases-column-header'>
                    <span className='version-releases-column-header-text'>Fire Time</span>
                    <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Time at which the Action Control is executed'}/></span>
                    </span>
                )
            },
            flex: 0.5,
            minWidth: 180,
            sortable: true,
            hideable: false,
            pinnable: false,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): React.ReactNode => {
                const rowData = params?.row;
                return rowData?.fireTime && rowData?.triggerEventType !== 'OfflinePending' && rowData?.actionStatus !== 'Started' ? formatRelativeTimeForTable(new Date(rowData?.fireTime)) : '-';
            },
        },
        {
            field: 'ruleName',
            headerName: 'Rule Name',
            flex: 1,
            minWidth: 120,
            sortable: true,
            hideable: false,
            pinnable: false,
            renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
                const rowData = params?.row;
                return (
                    <CustomTooltipWithLabel label={rowData?.ruleName} labelId={'advance__rule__engine__rule__name-' + rowData?.id} />
                )
            },
        },
        {
            field: 'chargerId',
            headerName: 'Charger',
            flex: 1,
            minWidth: 180,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
                const rowData = params?.row;
                return (
                    rowData?.chargerId ?
                        (<div
                            className='rule__engine__highlighted__text'
                            onClick={(e) => {
                                e?.stopPropagation()
                                const action = {
                                    type: chargerTypes.SET_CHARGER_REDUCER_STATE,
                                    isChargerTablePropsRetained: true
                                }
                                dispatch(action)
                            }
                            }
                        >
                            <LinkButton
                                linkBtnId={rowData?.chargerId}
                                link={'/charger?chargerId=' + rowData.chargerId}
                                btnClassName="individual__charger__text"
                                btnText={rowData?.chargerVisibleId}
                                linkBtnState={{ chargerId: rowData?.chargerId, charger: rowData }}
                            />
                        </div>) : (<div>{rowData?.chargerVisibleId}</div>)
                );
            },
        },
        {
            field: 'connectorType',
            headerName: 'Connector',
            flex: 1,
            minWidth: 120,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
                const rowData = params?.row
                return (
                    <CustomTooltipWithLabel label={rowData?.connectorType} labelId={'advance__rule__engine__connector-' + rowData?.id} />
                )
            }
        },
        {
            field: 'ocppId',
            headerName: 'OCPP Id',
            flex: 1,
            sortable: true,
            pinnable: false,
            minWidth: 120,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
                const rowData = params?.row
                return (
                    <CustomTooltipWithLabel label={rowData?.ocppId} labelId={rowData?.id} />
                )
            }
        },
        {
            field: 'chargingStationName',
            headerName: 'Charging Station',
            flex: 1,
            minWidth: 180,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
                const rowData = params?.row;
                return (
                    <div
                        className='rule__engine__highlighted__text'
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate('/station', { state: { id: rowData?.chargingStationId, mode: 'View', stationName: rowData?.chargingStationName, } });
                        }}>
                        <CustomTooltipWithLabel label={rowData?.chargingStationName} labelId={'rule__engine__history__charging__station__name'} />
                    </div>
                )
            },
        },
        {
            field: 'customerName',
            headerName: 'Customer',
            flex: 1,
            minWidth: 180,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
                const rowData = params?.row;
                return (
                    <div
                        className='rule__engine__highlighted__text'
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate('/customer', { state: { id: rowData?.customerId, mode: 'View', name: rowData?.customerName } });
                        }}>
                        <CustomTooltipWithLabel label={rowData?.customerName} labelId={'rule__engine__history__customer__name'} />

                    </div>
                )
            },
        },
        {
            field: 'triggerLog',
            headerName: 'Triggered Conditions',
            renderHeader: ()=>{
                return (
                    <span className='version-releases-column-header'>
                    <span className='version-releases-column-header-text'>Triggered Conditions</span>
                    <span className='version-releases-column-header-icon'><IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'Condition leading to the rule execution'}/></span>
                    </span>
                )
            },
            flex: 1,
            minWidth: 300,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
                const rowData = params?.row;
                const triggerLogs = JSON.parse(rowData?.triggerLog);
                const conditions = triggerLogs?.triggeredConditions ?? [];
                const label = conditions.join(' , ');
                return (
                    <CustomTooltipWithLabel label={label} labelId={'rule__engine__history__trigger__condition_logs'} />
                )
            },
        },
        {
            field: 'actionStatus',
            headerName: 'Action Controls Status',
            flex: 0.8,
            minWidth: 180,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
                const rowData = params?.row
                const actionStatus = rowData?.actionStatus;
                const status = actionStatus === 'Completed';

                return (
                    <div className='rule__name__status'>
                        {rowData?.actionType && <IconWithTooltip iconClassName={status ? 'status__active__user__icon' : 'status__inactive__user__icon'} />}
                        {rowData?.actionType ?? '-'}
                    </div>
                );
            },
        },
        {
            field: 'parameterName',
            headerName: 'Parameter',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
                const rowData = params?.row;
                return (
                    <CustomTooltipWithLabel label={rowData?.parameterName} labelId={'rule__engine__history__parameter__name'} />
                )
            },
        },
        {
            field: 'remoteCommand',
            headerName: 'Remote Command',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
                const rowData = params?.row;
                return (
                    <CustomTooltipWithLabel label={rowData?.remoteCommand} labelId={'rule__engine__history__remote__command__name'} />
                )
            },
        },
        {
            field: 'triggerEventType',
            headerName: 'Remarks',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
                const rowData = params?.row;
                const remark  = rowData?.triggerEventType === 'OfflinePending' ? 'Charger is offline' :  ''
                return (
                    <CustomTooltipWithLabel label={remark} labelId={'rule__engine__history__trigger__event_type'} />
                )
            },
        },
    ];

    const getTableRows = useCallback(() => {
        return ruleExecutionSummary !== null && ruleExecutionSummary?.length > 0 ? ruleExecutionSummary : []
    }, [ruleExecutionSummary]);


    const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, field: string, order: string, filterData, searchTerm) => {
        const tableProps = cloneDeep(ruleExecutionSummaryListTablePropsRef?.current);
        const filters = filterData ? filterData : getCurrentFiltersValues('advanceRuleExecutionSummary');

        await dispatch(
            fetchAdvanceRuleExecutionSummary({
                view: view,
                sortBy: field || tableProps?.sortBy,
                order: order || tableProps?.order,
                pageSize,
                pageNumber,
                filters: filters,
                ruleId: ruleId ?? null,
                searchTerm: null,
                fetchAllTriggers: showAllTriggers
            })
        );
        tableProps.view = view;
        tableProps.sortBy = field || tableProps?.sortBy;
        tableProps.order = order || tableProps?.order;
        tableProps.pageNumber = pageNumber;
        tableProps.pageSize = pageSize;
        await dispatch(setAdvanceRuleExecutionSummaryTableProps(tableProps));
    }, [showAllTriggers]);

    const fileDownloadSuccessToast = useCallback((): Id => {
        setExportingFile(false);
        return toast.success('Execution Summary logs downloaded.');
    }, []);

    const fileDownloadFailureToast = useCallback((): Id => {
        setExportingFile(false);
        return toast.warn('Something went wrong. Please export again.');
    }, []);

    const handleClose = (): void => {
        setExportAnchorEl(null);
    };

    const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
        if (exportingFile) {
            // If already exporting, prevent another export
            return;
        }
        try {
            const response: AxiosResponse = await dispatch(exportRuleExecutionSummary({
                fileType,
                ruleId: ruleId,
                sortBy: ruleExecutionSummaryListTablePropsRef?.current?.sortBy,
                order: ruleExecutionSummaryListTablePropsRef?.current?.order,
                filters: getCurrentFiltersValues('advanceRuleExecutionSummary', false),
                fetchAllTriggers: showAllTriggers
            }));

            if (response?.status === 202 || response?.status === 200) {
                downloadFile({ url: response?.data?.url });
                fileDownloadSuccessToast()
            } else {
                fileDownloadFailureToast();
                setExportAnchorEl(null);
            }
        } finally {
            // Ensure that exporting status is updated even if there's an error
            setExportingFile(false);
            setExportAnchorEl(null);

        }
    }, [exportingFile, showAllTriggers, fileDownloadSuccessToast, fileDownloadFailureToast]);

    const onResetButtonClick = useCallback(async () => {
        const res = await setFilterData(null, '', 'advanceRuleExecutionSummary', 'CLEAR', false, false, true)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
            if (ruleExecutionSummaryListTablePropsRef?.current) {
                await layoutCallback(1, ruleExecutionSummaryListTablePropsRef?.current?.pageSize, ruleExecutionSummaryListTablePropsRef?.current?.view, ruleExecutionSummaryListTablePropsRef?.current?.sortBy, ruleExecutionSummaryListTablePropsRef?.current?.order, res?.filters, ruleExecutionSummaryListTablePropsRef?.current?.searchTerm)
            }
        }
    }, [layoutCallback])

    const filterFooterBtns = useMemo(() => {
        return [
            {
                buttonText: 'Reset',
                buttonId: 'reset__execution__summary',
                btnClassName: 'secondary__btn',
                handleClick: (): Promise<void> => onResetButtonClick(),
                isDisabled: false,
                buttonVariant: 'outlined',
            },
        ];
    }, [onResetButtonClick]);

    const handleFilterDataChange = useCallback(async (data, filterKey: string, isGlobal: boolean) => {
        const tableProps = cloneDeep(ruleExecutionSummaryListTablePropsRef?.current);
        const res = await setFilterData(data, filterKey, 'advanceRuleExecutionSummary', 'SET', isGlobal)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
            await layoutCallback(
                1,
                tableProps?.pageSize,
                tableProps?.view,
                tableProps?.sortBy,
                tableProps?.order,
                res?.filters,
                tableProps?.searchTerm ?? '',
            )
        }
    }, [layoutCallback])


    const executionSummaryFilterProps: FilterProp[] = useMemo(() => {
        const executionSummaryFireTimeRangeValue = getFilterSelectedValue('advanceRuleEngineFireTimeRangeInterval','advanceRuleExecutionSummary', false);
        return ([
            {
                filterLabel: 'Charging Stations',
                filterType: 'dropdown',
                filterId: 'chargingStationId',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('chargingStationId','advanceRuleExecutionSummary', false),
                    selectOptions: chargingStationReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                        handleFilterDataChange(val, 'chargingStationId', false)
                    },
                    selectDropDownId: 'advance-rule-engine-history-chargingStationId-type-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Chargers',
                filterType: 'dropdown',
                filterId: 'chargerId',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('chargerId','advanceRuleExecutionSummary', false),
                    selectOptions: chargerVisibleIdReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                        handleFilterDataChange(val, 'chargerId', false)
                    },
                    selectDropDownId: 'advance-rule-engine-history-chargerId-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Fire Time',
                filterId: 'advanceRuleEngineFireTimeRangeInterval',
                filterType: 'dateFilter',
                dateFilterProps: {
                    datePickerId: 'advance-rule-engine-fire-time-date-picker',
                    datePickerClassName: '',
                    selectsRange: true,
                    startDate: executionSummaryFireTimeRangeValue?.[0]
                        ? new Date(executionSummaryFireTimeRangeValue?.[0])
                        : null,
                    endDate: executionSummaryFireTimeRangeValue?.[1]
                        ? new Date(executionSummaryFireTimeRangeValue?.[1])
                        : null,
                    onChange: (val): void => {
                        handleFilterDataChange(val, 'advanceRuleEngineFireTimeRangeInterval', false);
                    },
                }
            }
        ])
    },[screenFilters,chargingStationReferenceData,chargerVisibleIdReferenceData,advanceRuleEngineRuleNameReferenceData,handleFilterDataChange])


    return (
        <>
            <ThemeProvider theme={TableDashboardTheme}>
                <div className='advance__rule__engine__execution__summary__table__wrap' ref={tableWrapRef}
                style={{ height: 'calc(100dvh - 165px)'}}>
                    <TableLayout
                        gridColumns={executionSummaryColumns}
                        tableRows={getTableRows()}
                        layoutView='list'
                        key={'advance__rule__summary__execution'}
                        tableId={'advance__rule__summary__execution'}
                        tableClassName={'advance__rule__summary__execution'}
                        listLayoutCallBack={layoutCallback}
                        totalCount={ruleExecutionSummaryCount}
                        showLoader={ruleExecutionSummaryLoader}
                        showSkeleton={!ruleExecutionSummary}
                        totalCountText={'Execution'}
                        customNoDataComponent={NoDataComponentRuleEngineHistory}
                        pageNumber={ruleExecutionSummaryListTableProps?.pageNumber}
                        tablePageSize={ruleExecutionSummaryListTableProps?.pageSize}
                        sortField={ruleExecutionSummaryListTableProps?.sortBy}
                        sortOrder={ruleExecutionSummaryListTableProps?.order}
                        leftPinnedColumns={['triggerTime','fireTime']}
                    />
                    <Menu
                        id='export-option-dropdown'
                        anchorEl={exportAnchorEl}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        className='export__option__dropdown'
                        sx={{ top: '30px' }}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem>
                            <div className='export__btn-option' onClick={(e) => { e?.stopPropagation(); onExportBtnClick('csv') }}>
                                To CSV
                            </div>
                        </MenuItem>
                        <MenuItem>
                            <div className='export__btn-option' onClick={(e) => { e?.stopPropagation(); onExportBtnClick('xlsx') }}>
                                To Excel
                            </div>
                        </MenuItem>
                    </Menu>
                </div>
            </ThemeProvider>
            {
                executionSummaryFiltersVisible && <SidePopover
                    id={'advance__rule__engine__execution__summary__side__filters'}
                    open={executionSummaryFiltersVisible}
                    containerClassName='manage__rule_engine__filters'
                    handleClose={handleFiltersClosePopover}
                    headerText={'Add Filters'}
                    // eslint-disable-next-line react/no-children-prop
                    children={
                        <FilterLayout
                            screen={'executionSummaryRuleEngineForm'}
                            requiredReferenceDataCallbacks={['triggerCategory', 'triggerLevel', 'actionControls', 'chargerVisibleId', 'chargingStationId']}
                            filters={executionSummaryFilterProps}
                            handleFilterDataChange={handleFilterDataChange}
                            excludeGlobalFilters={false}
                        />
                    }
                    btnsList={filterFooterBtns}
                />
            }
        </>
    )
}

export default ExecutionSummaryPopover